import { PlusOutlined } from "@ant-design/icons";
import { Alert, Button, Col, Form, Input, Row, Select, Space, Table } from "antd";
import React, { useContext, useEffect, useState } from "react";
import { DynamicFields, StaticFields } from "../../../../Data/data_edit";
import { MyModal, StyledFormTitle } from "../../../../components/styled-components";
import { UserContext } from "../../../../context/userContext";
import { CheckInput, FormatDateYMDHMS } from "../../../../functions/function";
import { DELETE, PATCH, POST } from "../../../../services";
import {
  CHECKER_DELETE_SKU,
  CHECKER_GET_NAME_BY_SKU,
  CHECKER_GET_ORDER_DETAIL,
  CHECKER_GET_ORDER_LINE,
  CHECKER_INSERT_NEW_SKU,
  CHECKER_UPDATE_RUN_NUMBER,
  CHECKER_UPDATE_STATUS,
} from "../../../../services/path";
import "../style.css";

interface EditModalProps {
  visible: boolean;
  onCancel: () => void;
  order_no: string;
  tracking_no: string;
  no: number;
}

const EditModal: React.FC<EditModalProps> = ({ visible, onCancel, order_no, tracking_no, no }) => {
  const [staticData, setStaticData] = useState<StaticFields | null>(null);
  const [dynamicData, setDynamicData] = useState<DynamicFields[]>([]);
  const [selectedStatus, setSelectedStatus] = useState<number | null>(null);
  const [skuid, setSkuid] = useState("");
  const [qty, setQty] = useState("");
  const [error, setError] = useState<string>("");
  const [itemsDelete, setItemsDelete] = useState<DynamicFields[]>([]);
  const [itemsToAdd, setItemsToAdd] = useState<DynamicFields[]>([]);
  const { user } = useContext(UserContext);
  const handleDeleteRow = (runNumber: number) => {
    const rowToDelete = dynamicData.find((row) => row.run_number === runNumber);
    if (rowToDelete) {
      setItemsDelete((prevItems) => [...prevItems, rowToDelete]);
    }

    // Remove the deleted row from dynamicData and correct the run_number values
    const updatedDynamicData = dynamicData
      .filter((row) => row.run_number !== runNumber)
      .map((row, index) => ({ ...row, run_number: index + 1 }));

    setDynamicData(updatedDynamicData);
  };

  const handleAddClick = async () => {
    try {
      const cleanedSkuid = skuid.trim();
      const cleanedQty = qty.trim();
      const isDuplicate = dynamicData.some(
        (row) => row.sku === cleanedSkuid && row.new_add === true,
      );

      if (!cleanedSkuid) {
        setError("Please enter a SKUID.");
        return;
      }

      if (!cleanedQty || isNaN(Number(cleanedQty)) || parseInt(cleanedQty) <= 0) {
        setError("Please enter a valid quantity (positive number).");
        return;
      }

      if (isDuplicate) {
        setError("Duplicate SKUID. Please enter a unique SKUID OR New SKUID.");
        return;
      }

      if (skuid && qty) {
        const getNameApi = CHECKER_GET_NAME_BY_SKU();
        const response = await POST(getNameApi, { sku: skuid });
        if (response) {
          const newRow = {
            no: response.no,
            order_no: response.order_no,
            run_number: dynamicData.length + 1,
            sku: response.sku,
            item_name: response.name,
            new_add: true,
            size: response.size,
            qty: parseInt(qty),
            price: response.price,
          };

          setDynamicData([...dynamicData, newRow]);
          setItemsToAdd((prevItems) => [...prevItems, newRow]);
          setSkuid("");
          setQty("");
          setError("");
        } else {
          setError("Item name not found for the given SKUID.");
        }
      } else {
        setError("Please input SKUID and QTY.");
      }
    } catch (error) {
      console.error("Error fetching item name:", error);
      setError("Error fetching item name. Please try again later.");
    }
  };

  const handleSaveChange = async () => {
    try {
      // First, handle deletions
      for (const row of itemsDelete) {
        const deleteApi = CHECKER_DELETE_SKU();
        const deleteReq = {
          no: row.no,
          new_add: true,
          order_no: row.order_no,
        };

        const response = await DELETE(deleteApi, deleteReq);
        if (response !== true) {
          console.error("Error deleting row:", response);
        }
      }

      setItemsDelete([]);

      // Handle run_number updates
      const rowsToUpdate = dynamicData.filter((row) => row.new_add);
      const updateRequests = rowsToUpdate.map((row) => ({
        no: row.no,
        run_number: row.run_number,
        order_no: row.order_no,
      }));

      if (updateRequests.length) {
        const updateApi = CHECKER_UPDATE_RUN_NUMBER();
        const response = await PATCH(updateApi, updateRequests);
        if (response.status !== 200) {
          console.error("Error updating run_numbers:", response);
        }
      }

      for (const row of itemsToAdd) {
        const dataToSave = {
          sku: row.sku,
          item_name: row.item_name,
          size: row.size,
          qty: row.qty,
          order_no: order_no,
          alter_sku: "",
        };

        const saveApi = CHECKER_INSERT_NEW_SKU();
        const response = await POST(saveApi, dataToSave);
        if (response.status !== 200) {
          console.error("Error saving row:", response, " for data:", dataToSave);
        }
      }

      setItemsToAdd([]);
      onCancel();
      fetchData();
    } catch (error) {
      console.error("Error saving data:", error);
      setError("Failed to save data. Please try again.");
    }
  };

  interface AllowedTransitions {
    [key: string]: string[];
  }

  interface StatusOption {
    value: string;
    label: string;
  }
  // Define the status options with specific values and labels
  const statusOptions: StatusOption[] = [
    { value: "1", label: "ready" },
    { value: "2", label: "confirm" },
    { value: "3", label: "picked" },
    { value: "4", label: "packed" },
    { value: "5", label: "checked out" },
    { value: "12", label: "shipping" },
    { value: "6", label: "complete" },
    { value: "7", label: "waiting" },
    { value: "8", label: "cancel" },
    { value: "9", label: "return" },
    { value: "10", label: "hold" },
    { value: "11", label: "cancel-ship" },
    { value: "99", label: "unoperate" },
  ];

  const allowedTransitions: AllowedTransitions = {
    "1": ["1", "8"],
    "2": ["2", "7", "8"],
    "3": ["3", "8"],
    "4": ["4", "8"],
    "5": ["5", "8"],
    "6": ["6"],
    "7": ["2", "7", "8"],
    "8": ["2", "7", "8"],
    "9": ["1", "8", "9"],
    "10": ["8", "10"],
    "11": ["11"],
    "12": ["12", "8"],
    "99": ["1", "2", "8", "99"],
  };

  const isStatusChangeAllowed = (currentStatus: string, newStatus: string): boolean => {
    const allowedStatuses = allowedTransitions[currentStatus];
    return allowedStatuses ? allowedStatuses.includes(newStatus) : false;
  };

  const handleStatusChange = async (newStatus: string) => {
    try {
      const currentStatus: number | undefined = staticData?.operation_status_id;
      if (
        currentStatus !== undefined &&
        isStatusChangeAllowed(currentStatus.toString(), newStatus)
      ) {
        const API_UPDATE = CHECKER_UPDATE_STATUS();
        const Req = {
          no: staticData?.no,
          order_no: staticData?.order_no,
          operation_status_id: newStatus,
          modified_by: user.user_id,
        };

        if (Req.no !== undefined && Req.order_no !== undefined) {
          const updatedStatusResponse = await PATCH(API_UPDATE, Req);
          if (updatedStatusResponse.message === "Item updated successfully") {
            fetchData();
          } else {
            console.error("Error updating status:", updatedStatusResponse);
          }
        } else {
          console.error("Error: no or order_no is undefined.");
        }
      } else {
        console.log("Status change not allowed for the current status.");
      }
    } catch (error) {
      console.error("Error changing status:", error);
    }
  };

  const fetchData = async () => {
    try {
      const orderDetail = {
        no: no,
        order_no: order_no,
      };

      const staticDataResponse = await POST(CHECKER_GET_ORDER_DETAIL(), orderDetail);
      const orderLine = {
        order_no: order_no,
        tracking_no: tracking_no,
      };

      const getOrderLine = CHECKER_GET_ORDER_LINE();
      const dynamicDataResponse = await POST(getOrderLine, orderLine);
      setStaticData(staticDataResponse);
      setDynamicData(dynamicDataResponse);
      setStaticData(staticDataResponse.result[0] ?? staticDataResponse);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const tableStyles = {
    border: "1px solid #e8e8e8",
    borderRadius: "8px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.1)",
  };

  const columns = [
    {
      title: "รายการที่",
      dataIndex: "run_number",
      key: "run_number",
      render: (_: number, record: DynamicFields) => <span>{record.run_number}</span>,
    },
    {
      title: "รหัสสินค้า",
      dataIndex: "sku",
      key: "sku",
      render: (_: string, record: DynamicFields) => <span>{record.sku}</span>,
    },
    {
      title: "ชื่อสินค้า",
      dataIndex: "item_name",
      key: "item_name",
      render: (_: string, record: DynamicFields) => <span>{record.item_name}</span>,
    },
    {
      title: "จำนวน",
      dataIndex: "qty",
      key: "qty",
      render: (_: number, record: DynamicFields) => <span>{record.qty}</span>,
    },
    {
      title: "ราคา",
      dataIndex: "price",
      key: "price",
      render: (_: number, record: DynamicFields) => <span>{record.price}</span>,
    },
    {
      title: "ลบข้อมูล",
      key: "action",
      render: (_: string, record: DynamicFields) =>
        record.new_add ? (
          <Button onClick={() => handleDeleteRow(record.run_number)} type="primary" danger>
            Delete
          </Button>
        ) : (
          <Button disabled>Delete</Button>
        ),
    },
  ];

  return (
    <MyModal
      visible={visible}
      title="รายละเอียด Order"
      onCancel={onCancel}
      width={"70vw"}
      footer={[
        <Button key="cancel" onClick={onCancel}>
          ยกเลิก
        </Button>,
        <Button key="submit" type="primary" onClick={handleSaveChange}>
          บันทึก
        </Button>,
      ]}
    >
      <div className="responsive-container">
        {staticData !== null ? (
          <div className="details-container">
            <Form>
              <Row gutter={[16, 16]} justify="center">
                {/*Order Detail*/}
                <Col xs={24} sm={18} md={14} lg={12} xl={10} xxl={8} style={{ paddingLeft: "4vw" }}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <StyledFormTitle className="form-title">Order Information</StyledFormTitle>
                  </Form.Item>
                  <Form.Item
                    label="Order Created"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.order_create_date) ? "# " : "inherit",
                    }}
                  >
                    <span className="item-value">
                      {FormatDateYMDHMS(staticData.order_create_date)}
                    </span>
                  </Form.Item>
                  <Form.Item
                    label="Order no"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.order_no) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.order_no)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Brand"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.brand) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.brand)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Platform"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.platform) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.platform)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Channel"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.channel) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.channel)}</span>
                  </Form.Item>

                  <Form.Item
                    label="Current Status"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.operation_status_id) ? "#007bff" : "inherit",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Space.Compact style={{ width: "50%" }}>
                        <Select
                          defaultValue={
                            selectedStatus !== null
                              ? selectedStatus.toString()
                              : staticData.operation_status_id?.toString()
                          }
                          style={{ width: "10vw" }}
                          onChange={(value) => setSelectedStatus(Number(value))}
                          options={statusOptions.filter((option) =>
                            allowedTransitions[staticData?.operation_status_id]?.includes(
                              option.value,
                            ),
                          )}
                        />
                        <Button
                          type="primary"
                          style={{ marginRight: "3vw" }}
                          disabled={
                            selectedStatus === null ||
                            selectedStatus === staticData?.operation_status_id
                          }
                          onClick={() =>
                            handleStatusChange(selectedStatus ? selectedStatus.toString() : "")
                          } // eslint-disable-line
                        >
                          Confirm
                        </Button>
                      </Space.Compact>
                    </div>
                  </Form.Item>
                  <Form.Item
                    label="Total Price"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.total_price) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.total_price)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Discount"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.discount) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.discount)}</span>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={18} md={14} lg={12} xl={10} xxl={8} style={{ paddingLeft: "3vw" }}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <StyledFormTitle className="form-title">Batch Information</StyledFormTitle>
                  </Form.Item>
                  <Form.Item
                    label="Batch Created"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.batch_date) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{FormatDateYMDHMS(staticData.batch_date)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Batch Number"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.batch_id) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.batch_id)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Pick By"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.picker) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.picker)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Pick Date"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.pick_date) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{FormatDateYMDHMS(staticData.pick_date)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Cutting By"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.cutter) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.cutter)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Cutting Date"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.cutting_date) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{FormatDateYMDHMS(staticData.cutting_date)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Pack By"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.packer) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.packer)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Pack Date"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.pack_date) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{FormatDateYMDHMS(staticData.pack_date)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Pack Station ID"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.pack_station_id) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.pack_station_id)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Checkout By"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.checkout) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.checkout)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Checkout Date"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.checkout_date) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{FormatDateYMDHMS(staticData.checkout_date)}</span>
                  </Form.Item>
                </Col>

                <Col xs={24} sm={18} md={14} lg={12} xl={10} xxl={8} style={{ paddingTop: "2vw" }}>
                  <Form.Item style={{ marginBottom: 0 }}>
                    <StyledFormTitle className="form-title">Shipping Information</StyledFormTitle>
                  </Form.Item>
                  <Form.Item
                    label="Tracking Number"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tracking_no) ? "#007bff" : "inherit",
                    }}
                  >
                    {staticData.tracking_no !== null ? (
                      <span className="item-value" style={{ wordBreak: "break-all" }}>
                        {CheckInput(staticData.tracking_no)}
                      </span>
                    ) : (
                      <span className="item-value">-</span>
                    )}
                  </Form.Item>
                  <Form.Item
                    label="Logistic"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.logistic) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.logistic)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Shipping By"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.shipping_by) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.shipping_by)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Shipping Date"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color:
                        staticData.submit_at && CheckInput(staticData.shipping_at)
                          ? "#007bff"
                          : "inherit",
                    }}
                  >
                    <span className="item-value">
                      {staticData.submit_at ? CheckInput(staticData.shipping_at) : "-"}
                    </span>
                  </Form.Item>
                  <Form.Item
                    label="Shipping Lot"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.shipping_lot) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.shipping_lot)}</span>
                  </Form.Item>
                  <Form.Item
                    label="License Plate"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.license_plate) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.license_plate)}</span>
                  </Form.Item>
                </Col>

                <Col
                  xs={24}
                  sm={18}
                  md={14}
                  lg={12}
                  xl={10}
                  xxl={8}
                  style={{ paddingLeft: "3vw", paddingTop: "2vw" }}
                >
                  <Form.Item style={{ marginBottom: 0 }}>
                    <StyledFormTitle className="form-title">Tax Invoice</StyledFormTitle>
                  </Form.Item>
                  <Form.Item
                    label="Require Tax"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.is_req_inv) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.is_req_inv)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax ID"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_id) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_id)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax Customer"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_name_customer) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_name_customer)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax Address"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_address) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_address)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax District"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_district) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_district)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax Province"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_province) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_province)}</span>
                  </Form.Item>
                  <Form.Item
                    label="Tax Postcode"
                    labelCol={{ xs: { span: 24 }, sm: { span: 8 } }}
                    wrapperCol={{ xs: { span: 24 }, sm: { span: 16 } }}
                    style={{
                      fontWeight: "normal",
                      color: CheckInput(staticData.tax_post_code) ? "#007bff" : "inherit",
                    }}
                  >
                    <span className="item-value">{CheckInput(staticData.tax_post_code)}</span>
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </div>
        ) : (
          <p>Loading...</p>
        )}
      </div>
      {user.role_id === 1 && (
        <div style={{ display: "flex", alignItems: "center", marginTop: "16px" }}>
          <Form.Item label="SKUID" style={{ marginRight: "16px" }}>
            <Input
              style={{ width: "25vw" }}
              value={skuid}
              onChange={(e) => setSkuid(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="QTY" style={{ marginRight: "16px" }}>
            <Input style={{ width: "25vw" }} value={qty} onChange={(e) => setQty(e.target.value)} />
          </Form.Item>
          <Button
            icon={<PlusOutlined />}
            onClick={handleAddClick}
            style={{
              alignSelf: "flex-start",
              background: "#008000",
              borderColor: "#008000",
              color: "white",
            }}
          />
        </div>
      )}
      {error && <Alert message={error} type="error" style={{ marginBottom: "16px" }} />}
      <Table
        dataSource={dynamicData}
        columns={columns}
        pagination={false}
        className="custom-table"
        style={tableStyles}
      />
    </MyModal>
  );
};

export default EditModal;
